import { collectCursorPages } from '@app/utils/collectCursorPages';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import OrgModuleLayout from '../OrgModuleLayout';
import OrgModulePlaceholder from '../OrgModulePlaceholder';
import ListWorkspace, { type ListWorkspaceColumn, type ListWorkspaceProps } from '@app/components/ListWorkspace';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import StatusToast, { type StatusToastTone } from '@app/components/StatusToast';
import Tabs from '@app/components/Tabs';
import { KeyIcon, PlusIcon } from '@app/components/icons';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useCredentialAssignmentsApi, useCredentialsApi, useDevicesApi, useSecretsApi, useSitesApi } from '@app/hooks/useApis';
import type {
  Credential,
  CredentialAssignment,
  CredentialAssignmentsListResponse,
  CredentialsListResponse,
  Device,
  DevicesListResponse,
  Site,
  SitesListResponse
} from '@app/types/sdk';
import { ResponseError } from '@app/types/sdk';
import { formatDateTime } from '@app/utils/format';
import { collectCredentialSecretIds, deleteSecretWithAuth } from '@app/utils/secrets';
import CredentialWizard from './CredentialWizard';
import CredentialDrawer from './CredentialDrawer';
import AssignmentDrawer from './AssignmentDrawer';
import CredentialStorePanel from './CredentialStorePanel';

type TabId = 'credentials' | 'assignments' | 'credential-store';

type PendingDelete =
  | { kind: 'credentials'; rows: Credential[] }
  | { kind: 'assignments'; rows: CredentialAssignment[] };

interface CredentialFilters {
  q: string;
  kind: 'all' | Credential['kind'];
  secretState: 'all' | 'ok' | 'missing';
}

interface AssignmentFilters {
  q: string;
  scope: 'all' | 'org' | 'sites' | 'devices';
  credentialId: 'all' | string;
}


const DEFAULT_CREDENTIAL_FILTERS: CredentialFilters = {
  q: '',
  kind: 'all',
  secretState: 'all'
};

const DEFAULT_ASSIGNMENT_FILTERS: AssignmentFilters = {
  q: '',
  scope: 'all',
  credentialId: 'all'
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

type WorkspaceProps<T> = Omit<ListWorkspaceProps<T>, 'tabs' | 'activeTabId' | 'onTabChange'>;

const normalizeTab = (value: string | null | undefined): TabId => {
  const normalized = (value ?? '').trim();
  if (normalized === 'credentials' || normalized === 'assignments' || normalized === 'credential-store') {
    return normalized;
  }
  return 'credentials';
};

const CredentialsModule = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = useActiveOrgId();
  const credentialsApi = useCredentialsApi();
  const assignmentsApi = useCredentialAssignmentsApi();
  const sitesApi = useSitesApi();
  const devicesApi = useDevicesApi();
  const secretsApi = useSecretsApi();

  const [activeTab, setActiveTab] = useState<TabId>(() => normalizeTab(searchParams.get('tab')));
  const [credentialFilters, setCredentialFilters] = useState<CredentialFilters>({ ...DEFAULT_CREDENTIAL_FILTERS });
  const [credentialPage, setCredentialPage] = useState(1);
  const [credentialPageSize, setCredentialPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const [assignmentFilters, setAssignmentFilters] = useState<AssignmentFilters>({ ...DEFAULT_ASSIGNMENT_FILTERS });
  const [assignmentPage, setAssignmentPage] = useState(1);
  const [assignmentPageSize, setAssignmentPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const [credentialVersion, setCredentialVersion] = useState(0);
  const [assignmentVersion, setAssignmentVersion] = useState(0);
  const [scopeOptionsVersion, setScopeOptionsVersion] = useState(0);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [locallyDeletedCredentialIds, setLocallyDeletedCredentialIds] = useState<Set<string>>(() => new Set());
  const [locallyDeletedAssignmentIds, setLocallyDeletedAssignmentIds] = useState<Set<string>>(() => new Set());
  const [deleteToast, setDeleteToast] = useState<{ tone: StatusToastTone; message: string } | null>(null);

  useEffect(() => {
    setLocallyDeletedCredentialIds(new Set());
    setLocallyDeletedAssignmentIds(new Set());
  }, [orgId]);


  const credentialsFetcher = useMemo(
    () =>
      orgId
        ? async () => {
            const response = await credentialsApi.orgsOrgIdCredentialsGetRaw(
              { orgId, limit: credentialPageSize, after: `offset:${(credentialPage - 1) * credentialPageSize}`, q: credentialFilters.q.trim(), kind: credentialFilters.kind === 'all' ? undefined : credentialFilters.kind, secretState: credentialFilters.secretState === 'all' ? undefined : credentialFilters.secretState },
              { cache: 'no-store' }
            );
            return (await response.raw.json()) as CredentialsListResponse;
          }
        : null,
    [credentialsApi, orgId, credentialVersion, credentialPage, credentialPageSize, credentialFilters]
  );
  const credentialsQuery = useApiQuery<CredentialsListResponse>(credentialsFetcher, [credentialsFetcher]);

  const credentialCatalogFetcher = useMemo(() => orgId ? () => collectCursorPages<Credential>((after) =>
    credentialsApi.orgsOrgIdCredentialsGet({ orgId, limit: 200, after }, { cache: 'no-cache' })) : null,
    [credentialsApi, orgId, credentialVersion]);
  const credentialCatalogQuery = useApiQuery(credentialCatalogFetcher, [credentialCatalogFetcher]);
  const credentialCatalog = credentialCatalogQuery.data?.items ?? [];

  const assignmentsFetcher = useMemo(
    () =>
      orgId
        ? async () => {
            const response = await assignmentsApi.orgsOrgIdCredentialAssignmentsGetRaw(
              { orgId, limit: assignmentPageSize, after: `offset:${(assignmentPage - 1) * assignmentPageSize}`, q: assignmentFilters.q.trim(), scope: assignmentFilters.scope === 'all' ? undefined : assignmentFilters.scope, credentialId: assignmentFilters.credentialId === 'all' ? undefined : assignmentFilters.credentialId },
              { cache: 'no-store' }
            );
            return (await response.raw.json()) as CredentialAssignmentsListResponse;
          }
        : null,
    [assignmentsApi, orgId, assignmentVersion, assignmentPage, assignmentPageSize, assignmentFilters]
  );
  const assignmentsQuery = useApiQuery<CredentialAssignmentsListResponse>(assignmentsFetcher, [assignmentsFetcher]);

  const sitesFetcher = useMemo(
    () => (orgId ? () => collectCursorPages<SitesListResponse["items"][number]>((after) => sitesApi.orgsOrgIdSitesGet({ orgId, limit: 200, after }, { cache: 'no-cache' })) : null),
    [sitesApi, orgId, scopeOptionsVersion]
  );
  const sitesQuery = useApiQuery<{ items: SitesListResponse["items"]; meta: { exactTotal: number } }>(sitesFetcher, [sitesFetcher]);

  const devicesFetcher = useMemo(
    () => (orgId ? () => collectCursorPages((after) => devicesApi.orgsOrgIdDevicesGet({ orgId, limit: 200, after, includeRunningConfigStatus: false }, { cache: 'no-cache' })) : null),
    [devicesApi, orgId, scopeOptionsVersion]
  );
  const devicesQuery = useApiQuery<DevicesListResponse>(devicesFetcher, [devicesFetcher]);

  const credentials = useMemo(
    () => (credentialsQuery.data?.items ?? []).filter((item) => !item.id || !locallyDeletedCredentialIds.has(item.id)),
    [credentialsQuery.data?.items, locallyDeletedCredentialIds]
  );
  const assignments = useMemo(
    () => (assignmentsQuery.data?.items ?? []).filter((item) => !item.id || !locallyDeletedAssignmentIds.has(item.id)),
    [assignmentsQuery.data?.items, locallyDeletedAssignmentIds]
  );
  const sites = sitesQuery.data?.items ?? [];
  const devices = devicesQuery.data?.items ?? [];
  const knownDeviceIds = useMemo<Set<string> | null>(() => {
    if (!devicesQuery.data) {
      return null;
    }
    return new Set(devices.map((device) => device.id).filter((id): id is string => typeof id === 'string' && id.trim().length > 0));
  }, [devices, devicesQuery.data]);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState<'create' | 'duplicate'>('create');
  const [wizardCredential, setWizardCredential] = useState<Credential | null>(null);

  const [drawerCredentialId, setDrawerCredentialId] = useState<string | null>(() => {
    const fromQuery = searchParams.get('credentialId');
    return fromQuery && fromQuery.trim().length > 0 ? fromQuery.trim() : null;
  });

  const [assignmentDrawerOpen, setAssignmentDrawerOpen] = useState(false);
  const [assignmentDrawerMode, setAssignmentDrawerMode] = useState<'create' | 'edit'>('create');
  const [assignmentDrawerId, setAssignmentDrawerId] = useState<string | null>(null);
  const [assignmentInitial, setAssignmentInitial] = useState<CredentialAssignment | null>(null);
  const [defaultAssignmentCredentialId, setDefaultAssignmentCredentialId] = useState<string | null>(null);

  useEffect(() => {
    const tabFromQuery = normalizeTab(searchParams.get('tab'));
    if (tabFromQuery !== activeTab) {
      setActiveTab(tabFromQuery);
    }
    const credentialIdFromQuery = searchParams.get('credentialId');
    const normalizedCredentialId = credentialIdFromQuery && credentialIdFromQuery.trim().length > 0 ? credentialIdFromQuery.trim() : null;
    if (normalizedCredentialId !== drawerCredentialId) {
      setDrawerCredentialId(normalizedCredentialId);
    }
    if (normalizedCredentialId && tabFromQuery !== 'credentials') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', 'credentials');
          return next;
        },
        { replace: true }
      );
    }
  }, [activeTab, drawerCredentialId, searchParams, setSearchParams]);

  const filteredCredentials = credentials;
  const filteredAssignments = assignments;
  const credentialTotal = credentialsQuery.data?.meta?.exactTotal ?? credentials.length;
  const assignmentTotal = assignmentsQuery.data?.meta?.exactTotal ?? assignments.length;
  const credentialPageCount = Math.max(1, Math.ceil(credentialTotal / credentialPageSize));
  const assignmentPageCount = Math.max(1, Math.ceil(assignmentTotal / assignmentPageSize));

  useEffect(() => {
    setCredentialPage((prev) => Math.min(prev, credentialPageCount));
  }, [credentialPageCount]);

  useEffect(() => {
    setAssignmentPage((prev) => Math.min(prev, assignmentPageCount));
  }, [assignmentPageCount]);

  const credentialRows = filteredCredentials;

  const assignmentRows = filteredAssignments;

  const credentialColumns = useMemo<Array<ListWorkspaceColumn<Credential>>>(() => {
    return [
      {
        key: 'name',
        header: 'Name',
        render: (row) => (
          <div>
            <strong>{row.name ?? row.id}</strong>
            <div className="helper-text">{row.description ?? '—'}</div>
          </div>
        )
      },
      { key: 'kind', header: 'Kind', render: (row) => row.kind },
      {
        key: 'scope',
        header: 'Labels',
        render: (row) =>
          row.labels && Object.keys(row.labels).length > 0 ? (
            <div className="label-stack">
              {Object.entries(row.labels).map(([key, value]) => (
                <span key={key} className="ui-badge ui-badge--ghost">
                  {key}={value}
                </span>
              ))}
            </div>
          ) : (
            '—'
          )
      },
      {
        key: 'secret',
        header: 'Secrets',
        render: (row) => {
          const status = evaluateSecretStatus(row);
          const tone = status === 'OK' ? 'ui-badge--success' : 'ui-badge--danger';
          return <span className={`ui-badge ${tone}`}>{status}</span>;
        }
      },
      {
        key: 'usage',
        header: 'Assignments',
        render: (row) => String(row.assignmentCount ?? 0)
      },
      {
        key: 'updated',
        header: 'Last updated',
        render: (row) => formatDateTime(row.updatedAt ?? row.createdAt)
      }
    ];
  }, []);

  const assignmentColumns = useMemo<Array<ListWorkspaceColumn<CredentialAssignment>>>(() => {
    return [
      {
        key: 'name',
        header: 'Assignment',
        render: (row) => (
          <div>
            <strong>{row.name ?? row.id}</strong>
            <div className="helper-text">{row.description ?? '—'}</div>
          </div>
        )
      },
      {
        key: 'credential',
        header: 'Credential',
        render: (row) => {
          const credential = credentialCatalog.find((item) => item.id === row.credentialId);
          return credential?.name ?? row.credentialId ?? '—';
        }
      },
      {
        key: 'scope',
        header: 'Scope',
        render: (row) => describeScope(row.scope, knownDeviceIds)
      },
      {
        key: 'match',
        header: 'Filters',
        render: (row) => summarizeFilters(row)
      },
      {
        key: 'updated',
        header: 'Updated',
        render: (row) => formatDateTime(row.updatedAt ?? row.createdAt)
      }
    ];
  }, [credentialCatalog, knownDeviceIds]);

  const handleCredentialRowClick = useCallback(
    (row: Credential) => {
      if (!row.id) {
        return;
      }
      setActiveTab('credentials');
      setDrawerCredentialId(row.id);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', 'credentials');
          next.set('credentialId', row.id ?? '');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleAssignmentRowClick = useCallback(
    (row: CredentialAssignment) => {
      setScopeOptionsVersion((prev) => prev + 1);
      setAssignmentDrawerMode('edit');
      setAssignmentDrawerId(row.id ?? null);
      setAssignmentInitial(row);
      setDefaultAssignmentCredentialId(row.credentialId ?? null);
      setAssignmentDrawerOpen(true);
    },
    []
  );

  const resetCredentialPagination = useCallback(() => {
    setCredentialPage(1);
  }, []);

  const resetAssignmentPagination = useCallback(() => {
    setAssignmentPage(1);
  }, []);

  const credentialFiltersActive =
    credentialFilters.q.trim().length > 0 || credentialFilters.kind !== 'all' || credentialFilters.secretState !== 'all';

  const assignmentFiltersActive =
    assignmentFilters.q.trim().length > 0 || assignmentFilters.scope !== 'all' || assignmentFilters.credentialId !== 'all';

  const credentialFilterBar = (
    <div className="list-workspace__filter-grid">
      <label className="form-field">
        <span className="form-field__label">Search</span>
        <input
          type="text"
          className="input"
          value={credentialFilters.q}
          onChange={(event) => {
            setCredentialFilters((prev) => ({ ...prev, q: event.target.value }));
            resetCredentialPagination();
          }}
          placeholder="Search by name, labels, or kind"
        />
      </label>
      <label className="form-field">
        <span className="form-field__label">Kind</span>
        <select
          className="select"
          value={credentialFilters.kind}
          onChange={(event) => {
            setCredentialFilters((prev) => ({ ...prev, kind: event.target.value as CredentialFilters['kind'] }));
            resetCredentialPagination();
          }}
        >
          <option value="all">All kinds</option>
          {Array.from(new Set(credentialCatalog.map((credential) => credential.kind))).map((kind) => (
            <option key={kind} value={kind}>
              {kind}
            </option>
          ))}
        </select>
      </label>
      <label className="form-field">
        <span className="form-field__label">Secrets</span>
        <select
          className="select"
          value={credentialFilters.secretState}
          onChange={(event) => {
            setCredentialFilters((prev) => ({ ...prev, secretState: event.target.value as CredentialFilters['secretState'] }));
            resetCredentialPagination();
          }}
        >
          <option value="all">All statuses</option>
          <option value="ok">Authentication material configured</option>
          <option value="missing">Authentication material missing</option>
        </select>
      </label>
    </div>
  );

  const assignmentFilterBar = (
    <div className="list-workspace__filter-grid">
      <label className="form-field">
        <span className="form-field__label">Search</span>
        <input
          type="text"
          className="input"
          value={assignmentFilters.q}
          onChange={(event) => {
            setAssignmentFilters((prev) => ({ ...prev, q: event.target.value }));
            resetAssignmentPagination();
          }}
          placeholder="Search assignments"
        />
      </label>
      <label className="form-field">
        <span className="form-field__label">Scope</span>
        <select
          className="select"
          value={assignmentFilters.scope}
          onChange={(event) => {
            setAssignmentFilters((prev) => ({ ...prev, scope: event.target.value as AssignmentFilters['scope'] }));
            resetAssignmentPagination();
          }}
        >
          <option value="all">All scopes</option>
          <option value="org">Organization</option>
          <option value="sites">Sites</option>
          <option value="devices">Devices</option>
        </select>
      </label>
      <label className="form-field">
        <span className="form-field__label">Credential</span>
        <select
          className="select"
          value={assignmentFilters.credentialId}
          onChange={(event) => {
            setAssignmentFilters((prev) => ({ ...prev, credentialId: event.target.value as AssignmentFilters['credentialId'] }));
            resetAssignmentPagination();
          }}
        >
          <option value="all">All credentials</option>
          {credentialCatalog.map((credential) => (
            <option key={credential.id} value={credential.id ?? ''}>
              {credential.name ?? credential.id}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  const refreshCredentials = useCallback(() => {
    setCredentialVersion((prev) => prev + 1);
  }, []);

  const refreshAssignments = useCallback(() => {
    setAssignmentVersion((prev) => prev + 1);
  }, []);
  const refreshScopeOptions = useCallback(() => {
    setScopeOptionsVersion((prev) => prev + 1);
  }, []);

  const openCreateAssignment = useCallback((credentialId: string | null = null) => {
    refreshScopeOptions();
    setAssignmentDrawerMode('create');
    setAssignmentDrawerId(null);
    setAssignmentInitial(null);
    setDefaultAssignmentCredentialId(credentialId);
    setAssignmentDrawerOpen(true);
  }, [refreshScopeOptions]);

  const handleRequestAssignment = useCallback(
    (record: Credential) => {
      setDrawerCredentialId(null);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('credentialId');
          next.set('tab', 'assignments');
          return next;
        },
        { replace: true }
      );
      openCreateAssignment(record.id ?? null);
    },
    [openCreateAssignment, setSearchParams]
  );

  const handleCredentialCreated = useCallback(
    (record: Credential) => {
      setWizardOpen(false);
      refreshCredentials();
    },
    [refreshCredentials]
  );

  const handleCredentialUpdated = useCallback(
    (record: Credential) => {
      refreshCredentials();
    },
    [refreshCredentials]
  );

  const handleAssignmentSaved = useCallback(
    (record: CredentialAssignment) => {
      setAssignmentDrawerOpen(false);
      setDefaultAssignmentCredentialId(null);
      refreshAssignments();
    },
    [refreshAssignments]
  );

  const performDeleteCredentials = useCallback(
    async (rows: Credential[]) => {
      if (!orgId || rows.length === 0) {
        return;
      }
      const deletedCredentialIds = new Set<string>();
      let failedCount = 0;
      let cleanupFailed = false;
      for (const row of rows) {
        if (!row.id) {
          continue;
        }
        try {
          const recordResponse = await credentialsApi.orgsOrgIdCredentialsCredentialIdGetRaw({ orgId, credentialId: row.id });
          const record = await recordResponse.value();
          const etag = recordResponse.raw.headers.get('ETag');
          if (!etag) {
            throw new Error('Unable to resolve credential version. Reload and try again.');
          }
          const secretIds = Array.from(new Set(collectCredentialSecretIds(record)));
          await credentialsApi.orgsOrgIdCredentialsCredentialIdDelete({ orgId, credentialId: row.id, ifMatch: etag });
          let cleanupWarning: string | null = null;
          if (secretIds.length > 0) {
            for (const secretId of secretIds) {
              try {
                await deleteSecretWithAuth({ secretsApi, orgId, secretId });
              } catch (cleanupError) {
                if (cleanupError instanceof ResponseError) {
                  cleanupWarning = `Credential deleted but failed to delete secret ${secretId} (HTTP ${cleanupError.response.status}).`;
                } else if (cleanupError instanceof Error) {
                  cleanupWarning = `Credential deleted but failed to delete secret ${secretId}. ${cleanupError.message}`;
                } else {
                  cleanupWarning = 'Credential deleted but failed to delete an associated secret.';
                }
              }
            }
          }
          deletedCredentialIds.add(row.id);
          if (cleanupWarning) {
            console.warn(cleanupWarning);
            cleanupFailed = true;
          }
        } catch (error) {
          failedCount += 1;
          const message = error instanceof Error ? error.message : 'Failed to delete credential.';
          console.error(message);
        }
      }
      if (deletedCredentialIds.size > 0) {
        setLocallyDeletedCredentialIds((current) => new Set([...current, ...deletedCredentialIds]));
        setPendingDelete(null);
        refreshCredentials();
        refreshAssignments();
        if (drawerCredentialId && deletedCredentialIds.has(drawerCredentialId)) {
          setDrawerCredentialId(null);
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('credentialId');
              return next;
            },
            { replace: true }
          );
        }
        const warning = cleanupFailed || failedCount > 0;
        setDeleteToast({
          tone: warning ? 'warning' : 'success',
          message: cleanupFailed
            ? `${deletedCredentialIds.size} credential${deletedCredentialIds.size === 1 ? '' : 's'} deleted, but associated secret cleanup could not be completed.`
            : failedCount > 0
              ? `${deletedCredentialIds.size} of ${rows.length} credentials deleted. ${failedCount} could not be deleted.`
              : `${deletedCredentialIds.size} credential${deletedCredentialIds.size === 1 ? '' : 's'} deleted successfully.`
        });
      } else {
        setDeleteToast({ tone: 'error', message: 'Failed to delete the selected credentials.' });
      }
    },
    [credentialsApi, drawerCredentialId, orgId, refreshAssignments, refreshCredentials, secretsApi, setSearchParams]
  );

  const performDeleteAssignments = useCallback(
    async (rows: CredentialAssignment[]) => {
      if (!orgId || rows.length === 0) {
        return;
      }
      const deletedAssignmentIds = new Set<string>();
      let failedCount = 0;
      for (const row of rows) {
        if (!row.id) {
          continue;
        }
        try {
          const response = await assignmentsApi.orgsOrgIdCredentialAssignmentsAssignmentIdGetRaw({ orgId, assignmentId: row.id });
          const etag = response.raw.headers.get('ETag');
          if (!etag) {
            throw new Error('Unable to resolve assignment version. Reload and try again.');
          }
          await assignmentsApi.orgsOrgIdCredentialAssignmentsAssignmentIdDelete({ orgId, assignmentId: row.id, ifMatch: etag });
          deletedAssignmentIds.add(row.id);
        } catch (error) {
          failedCount += 1;
          const message = error instanceof Error ? error.message : 'Failed to delete assignment.';
          console.error(message);
        }
      }
      if (deletedAssignmentIds.size > 0) {
        setLocallyDeletedAssignmentIds((current) => new Set([...current, ...deletedAssignmentIds]));
        setPendingDelete(null);
        refreshAssignments();
        if (assignmentDrawerId && deletedAssignmentIds.has(assignmentDrawerId)) {
          setAssignmentDrawerOpen(false);
          setAssignmentDrawerId(null);
          setAssignmentInitial(null);
        }
        setDeleteToast({
          tone: failedCount > 0 ? 'warning' : 'success',
          message:
            failedCount > 0
              ? `${deletedAssignmentIds.size} of ${rows.length} assignments deleted. ${failedCount} could not be deleted.`
              : `${deletedAssignmentIds.size} assignment${deletedAssignmentIds.size === 1 ? '' : 's'} deleted successfully.`
        });
      } else {
        setDeleteToast({ tone: 'error', message: 'Failed to delete the selected assignments.' });
      }
    },
    [assignmentDrawerId, assignmentsApi, orgId, refreshAssignments]
  );

  if (!orgId) {
    return (
      <OrgModuleLayout title="Credentials" description="Manage device credentials and routing assignments.">
        <OrgModulePlaceholder intent="Select an organization to manage credentials." />
      </OrgModuleLayout>
    );
  }

  if (credentialsQuery.backendMissing || assignmentsQuery.backendMissing) {
    return (
      <OrgModuleLayout title="Credentials" description="Manage device credentials and routing assignments.">
        <OrgModulePlaceholder intent="Implement the credentials and assignments endpoints to populate this module." />
      </OrgModuleLayout>
    );
  }

  const credentialWorkspace: WorkspaceProps<Credential> = {
    headerLabel: 'Credentials',
    rows: credentialRows,
    columns: credentialColumns,
    rowId: (row, index) => row.id ?? index,
    highlightRowId: drawerCredentialId,
    onRowClick: handleCredentialRowClick,
    rowActions: (row) =>
      row.id ? (
        <button
          type="button"
          className="list-workspace__icon-button"
          aria-label="Create assignment"
          title="Create assignment"
          onClick={(event) => {
            event.stopPropagation();
            openCreateAssignment(row.id ?? null);
          }}
        >
          <KeyIcon size={16} />
        </button>
      ) : null,
    onAdd: () => {
      setWizardMode('create');
      setWizardCredential(null);
      setWizardOpen(true);
    },
    onDeleteSelected: (rows) => setPendingDelete({ kind: 'credentials', rows }),
    onRefresh: refreshCredentials,
    loading: credentialsQuery.loading,
    headerStatus:
      credentialsQuery.error ? <div className="list-workspace__status">Failed to load credentials: {credentialsQuery.error.message}</div> : undefined,
    filterBar: credentialFilterBar,
    filterActive: credentialFiltersActive,
    onFilterReset: () => {
      setCredentialFilters({ ...DEFAULT_CREDENTIAL_FILTERS });
      resetCredentialPagination();
    },
    page: credentialPage,
    pageSize: credentialPageSize,
    totalCount: credentialTotal,
    onPageChange: (page) => setCredentialPage(Math.min(Math.max(1, page), credentialPageCount)),
    onPageSizeChange: (size) => {
      setCredentialPageSize(size);
      setCredentialPage(1);
    },
    selectionDisabled: false,
    emptyState: credentialsQuery.loading ? <p>Loading credentials…</p> : <p>No credentials yet.</p>,
    toolbarActions: undefined
  };

  const assignmentWorkspace: WorkspaceProps<CredentialAssignment> = {
    headerLabel: 'Assignments',
    rows: assignmentRows,
    columns: assignmentColumns,
    rowId: (row, index) => row.id ?? index,
    highlightRowId: assignmentDrawerId,
    onRowClick: handleAssignmentRowClick,
    onAdd: () => openCreateAssignment(null),
    onDeleteSelected: (rows) => setPendingDelete({ kind: 'assignments', rows }),
    onRefresh: refreshAssignments,
    loading: assignmentsQuery.loading,
    headerStatus:
      assignmentsQuery.error ? <div className="list-workspace__status">Failed to load assignments: {assignmentsQuery.error.message}</div> : undefined,
    filterBar: assignmentFilterBar,
    filterActive: assignmentFiltersActive,
    onFilterReset: () => {
      setAssignmentFilters({ ...DEFAULT_ASSIGNMENT_FILTERS });
      resetAssignmentPagination();
    },
    page: assignmentPage,
    pageSize: assignmentPageSize,
    totalCount: assignmentTotal,
    onPageChange: (page) => setAssignmentPage(Math.min(Math.max(1, page), assignmentPageCount)),
    onPageSizeChange: (size) => {
      setAssignmentPageSize(size);
      setAssignmentPage(1);
    },
    selectionDisabled: false,
    emptyState: assignmentsQuery.loading ? <p>Loading assignments…</p> : <p>No assignments yet.</p>
  };

  const tabItems = [
    { id: 'credentials', label: 'Credentials' },
    { id: 'assignments', label: 'Assignments' },
    { id: 'credential-store', label: 'Credential Store' }
  ];

  const handleTabChange = useCallback(
    (id: string) => {
      const nextTab = normalizeTab(id);
      setActiveTab(nextTab);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', nextTab);
          if (nextTab !== 'credentials') {
            next.delete('credentialId');
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const activeWorkspace = activeTab === 'credentials' ? credentialWorkspace : assignmentWorkspace;
  const workspaceProps = activeWorkspace as WorkspaceProps<any>;

  const confirmDelete = async () => {
    if (!pendingDelete || deleteBusy) {
      return;
    }
    setDeleteBusy(true);
    if (pendingDelete.kind === 'credentials') {
      await performDeleteCredentials(pendingDelete.rows);
    } else {
      await performDeleteAssignments(pendingDelete.rows);
    }
    setDeleteBusy(false);
  };

  return (
    <OrgModuleLayout title="Credentials" description="Credential catalog with embedded secret rotation and assignments.">
      <Tabs items={tabItems} activeId={activeTab} onChange={handleTabChange} />
      {activeTab === 'credential-store' ? (
        <CredentialStorePanel />
      ) : (
        <ListWorkspace {...workspaceProps} />
      )}

      <CredentialWizard
        orgId={orgId}
        open={wizardOpen}
        mode={wizardMode}
        initialCredential={wizardCredential}
        onClose={() => setWizardOpen(false)}
        onCompleted={(record) => handleCredentialCreated(record)}
      />

      <CredentialDrawer
        orgId={orgId}
        credentialId={drawerCredentialId}
        open={Boolean(drawerCredentialId)}
        onClose={() => {
          setDrawerCredentialId(null);
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('credentialId');
              return next;
            },
            { replace: true }
          );
        }}
        onUpdated={(record) => handleCredentialUpdated(record)}
        onRequestAssignment={handleRequestAssignment}
      />

      <AssignmentDrawer
        orgId={orgId}
        open={assignmentDrawerOpen}
        mode={assignmentDrawerMode}
        assignmentId={assignmentDrawerId}
        initialAssignment={assignmentInitial}
        defaultCredentialId={defaultAssignmentCredentialId}
        credentialOptions={credentialCatalog}
        siteOptions={sites as Site[]}
        deviceOptions={devices as Device[]}
        existingAssignments={assignments}
        onClose={() => {
          setAssignmentDrawerOpen(false);
          setDefaultAssignmentCredentialId(null);
          setAssignmentDrawerId(null);
          setAssignmentInitial(null);
        }}
        onSaved={(record) => handleAssignmentSaved(record)}
      />
      <DeleteConfirmationModal
        open={pendingDelete != null}
        title={
          pendingDelete?.kind === 'credentials'
            ? pendingDelete.rows.length === 1 ? 'Delete credential?' : `Delete ${pendingDelete.rows.length} credentials?`
            : pendingDelete?.rows.length === 1 ? 'Delete assignment?' : `Delete ${pendingDelete?.rows.length ?? 0} assignments?`
        }
        description={
          pendingDelete?.kind === 'credentials'
            ? 'This permanently removes the selected credentials and attempts to clean up their associated secrets.'
            : 'This permanently removes the selected credential assignments.'
        }
        deleting={deleteBusy}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      />
      {deleteToast && <StatusToast tone={deleteToast.tone} message={deleteToast.message} occurrence={deleteToast} onDismiss={() => setDeleteToast(null)} />}
    </OrgModuleLayout>
  );
};

const evaluateSecretStatus = (credential: Credential): string => {
  switch (credential.kind) {
    case 'cli_ssh': {
      const details = credential.details as any;
      if (details.cyberarkStoreId) {
        return 'OK';
      }
      const hasPassword = Boolean(details.execPasswordSecretId);
      const hasKey = Boolean(details.sshPrivateKeySecretId);
      if (hasPassword || hasKey) {
        return 'OK';
      }
      return 'Missing';
    }
    case 'gnmi': {
      const details = credential.details as any;
      return details.passwordSecretId && details.deviceServerTrustCaBundleSecretId ? 'OK' : 'Missing';
    }
    case 'snmp_v2c': {
      const details = credential.details as any;
      return details.readCommunitySecretId ? 'OK' : 'Missing';
    }
    case 'snmp_v3': {
      const details = credential.details as any;
      if (details.securityLevel === 'noAuthNoPriv') {
        return 'OK';
      }
      if (details.securityLevel === 'authNoPriv') {
        return details.authPasswordSecretId ? 'OK' : 'Missing';
      }
      return details.authPasswordSecretId && details.privPasswordSecretId ? 'OK' : 'Missing';
    }
    case 'api_rest': {
      const details = credential.details as any;
      const auth = details.auth ?? {};
      switch (auth.type) {
        case 'api_key':
          return auth.apiKey?.apiKeySecretId ? 'OK' : 'Missing';
        case 'basic':
          return auth.basic?.passwordSecretId ? 'OK' : 'Missing';
        case 'bearer':
          return auth.bearer?.tokenSecretId ? 'OK' : 'Missing';
        case 'mtls':
          if (auth.mtls?.clientCertSecretId && auth.mtls?.clientKeySecretId) {
            return 'OK';
          }
          if (auth.mtls?.pkcs12SecretId) {
            return 'OK';
          }
          return 'Missing';
        default:
          return '—';
      }
    }
    default:
      return '—';
  }
};

const resolveScopeType = (scope: CredentialAssignment['scope'] | undefined): AssignmentFilters['scope'] => {
  if (!scope) {
    return 'org';
  }
  const scopeType = (scope as any).type;
  if (typeof scopeType === 'string' && (scopeType === 'org' || scopeType === 'sites' || scopeType === 'devices')) {
    return scopeType;
  }
  if (
    scopeType &&
    typeof scopeType === 'object' &&
    typeof scopeType.type === 'string' &&
    (scopeType.type === 'org' || scopeType.type === 'sites' || scopeType.type === 'devices')
  ) {
    return scopeType.type;
  }
  return 'org';
};

const describeScope = (scope: CredentialAssignment['scope'] | undefined, knownDeviceIds: Set<string> | null = null): string => {
  if (!scope) {
    return '—';
  }
  const anyScope = scope as any;
  const scopeType = resolveScopeType(scope);
  if (scopeType === 'org') {
    return 'Organization';
  }
  if (scopeType === 'sites') {
    return `Sites (${extractScopeIds(anyScope.siteIds).length})`;
  }
  if (scopeType === 'devices') {
    const deviceIds = extractScopeIds(anyScope.deviceIds);
    if (knownDeviceIds == null) {
      return `Devices (${deviceIds.length})`;
    }
    return `Devices (${deviceIds.filter((id) => knownDeviceIds.has(id)).length})`;
  }
  return '—';
};

const extractScopeIds = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }
  if (value && typeof value === 'object' && Array.isArray((value as { value?: unknown }).value)) {
    return ((value as { value: unknown[] }).value).filter((item): item is string => typeof item === 'string');
  }
  return [];
};

const summarizeFilters = (row: CredentialAssignment): string => {
  if (!row.match) {
    return '—';
  }
  const parts: string[] = [];
  if (row.match.hostname?.regex) {
    parts.push(`Regex ${row.match.hostname.regex}`);
  }
  if (row.match.hostname?.prefixAny?.length) {
    parts.push(`Prefix ${row.match.hostname.prefixAny.join(', ')}`);
  }
  if (row.match.hostname?.suffixAny?.length) {
    parts.push(`Suffix ${row.match.hostname.suffixAny.join(', ')}`);
  }
  if (row.match.labelsAll && Object.keys(row.match.labelsAll).length > 0) {
    parts.push(
      `Labels ${Object.entries(row.match.labelsAll)
        .map(([k, v]) => `${k}=${v}`)
        .join(', ')}`
    );
  }
  if (row.match.vendorsAny?.length) {
    parts.push(`Vendors ${row.match.vendorsAny.join(', ')}`);
  }
  if (row.match.modelsAny?.length) {
    parts.push(`Models ${row.match.modelsAny.join(', ')}`);
  }
  return parts.length > 0 ? parts.join(' • ') : 'None';
};

export default CredentialsModule;
